import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { type PublicClip } from '@air/api';

import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { type GalleryAssetProps } from '~/components/v2/components/shared/gallery/GalleryAsset';
import { usePublicGetAvailableCustomFields } from '~/components/v2/hooks/usePublicGetAvailableCustomFields';
import { QueryParamNames } from '~/constants/search';
import { useGoToPublicAssetPage } from '~/hooks/useGoToPublicAssetPage';
import { useOpenAssetInNewTab } from '~/hooks/useOpenAssetInNewTab';
import { useURLLibraryIdSelector } from '~/hooks/useURLLibraryIdSelector';
import { AssetModalPanel } from '~/providers/AssetModalPanelContextProvider';
import { usePublicCustomFieldsContext } from '~/providers/PublicCustomFieldsProvider';
import { centralizedBoardLibraryIdSelector } from '~/store/centralizedBoard/selectors';
import {
  canSeeAssetCustomFields,
  canSeeAssetVersions,
  canViewAssetDiscussions,
} from '~/utils/permissions/assetPermissions';

export type UsePublicGalleryAssetCardPropsParams = {
  asset: PublicClip;
};

export type PublicGalleryAssetCardProps = Pick<
  GalleryAssetProps<PublicClip>,
  | 'allCustomFields'
  | 'canViewCustomFields'
  | 'onAssetClick'
  | 'onAssetTitleCmdClick'
  | 'onCustomFieldsClick'
  | 'onDiscussionsClick'
  | 'onVersionsClick'
  | 'shouldShowDiscussions'
  | 'showVersionsIndicator'
>;

/**
 * The public asset card's BODY contract — every GalleryAsset prop that shapes what the card looks
 * like and where clicking it goes, derived from the public permission/custom-field/navigation
 * hooks. Shared by PublicGalleryAsset (public pages) and ShowcaseEditorGalleryAsset (the
 * editor's edit canvas) so the canvas card stays pixel-identical to the public card (WYSIWYG);
 * only the menu getters — deliberately NOT part of this contract — differ between the two.
 */
export const usePublicGalleryAssetCardProps = ({
  asset,
}: UsePublicGalleryAssetCardPropsParams): PublicGalleryAssetCardProps => {
  const { permissions } = usePublicPermissions();
  const { customFields } = usePublicCustomFieldsContext();
  const { goToAssetPage } = useGoToPublicAssetPage();
  const { openAssetInNewTab } = useOpenAssetInNewTab();
  const urlLibraryId = useURLLibraryIdSelector();
  const libraryId = useSelector(centralizedBoardLibraryIdSelector);
  const { getAvailableCustomFields } = usePublicGetAvailableCustomFields({
    libraryId: libraryId || urlLibraryId,
  });

  const onCustomFieldsClick = useCallback(
    () =>
      goToAssetPage({
        asset,
        query: {
          [QueryParamNames.amTab]: AssetModalPanel.FIELDS,
        },
        trackLocation: 'custom-field-click',
      }),
    [asset, goToAssetPage],
  );

  const onDiscussionsClick = useCallback(
    () =>
      goToAssetPage({
        asset,
        query: {
          [QueryParamNames.amTab]: AssetModalPanel.COMMENTS,
        },
        trackLocation: 'comments-click',
      }),
    [asset, goToAssetPage],
  );

  const onVersionsClick = useCallback(
    () =>
      goToAssetPage({
        asset,
        query: {
          [QueryParamNames.versions]: true,
        },
        trackLocation: 'versions-click',
      }),
    [asset, goToAssetPage],
  );

  const onAssetClick = useCallback(
    () =>
      goToAssetPage({
        asset,
        trackLocation: 'card-click',
      }),
    [asset, goToAssetPage],
  );

  const onAssetTitleCmdClick = useCallback(
    () =>
      openAssetInNewTab({
        asset,
        trackLocation: 'card-click',
      }),
    [asset, openAssetInNewTab],
  );

  const canViewAssetVersions = canSeeAssetVersions(permissions);

  return {
    allCustomFields: getAvailableCustomFields(customFields),
    canViewCustomFields: canSeeAssetCustomFields(permissions),
    onAssetClick,
    onAssetTitleCmdClick,
    onCustomFieldsClick,
    onDiscussionsClick,
    onVersionsClick,
    shouldShowDiscussions: canViewAssetDiscussions(permissions),
    showVersionsIndicator: canViewAssetVersions && !!asset.assetVersionCount && asset.assetVersionCount > 1,
  };
};
